/**
 * ScheduleGateModal — the "warn but allow" confirm shown when a recruiter moves a
 * lead into a scheduling stage (Ready to Schedule / Scheduled) before the study's
 * required prescreening milestones are met. It lists the unmet requirements and
 * takes an optional reason; confirming rides { schedule_gate_override, note } on
 * the lifecycle PATCH, which records the override on the timeline. Cancelling
 * leaves the lead's status unchanged.
 */
import { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { lifecycleMeta } from '../lib';
import { Z } from './ui';

interface Props {
  /** The scheduling status being moved to (drives the title). */
  targetStatus: string;
  /** Required milestones not yet met. */
  unmet: { key: string; label: string }[];
  onConfirm: (note: string) => void;
  onCancel: () => void;
}

export default function ScheduleGateModal({ targetStatus, unmet, onConfirm, onCancel }: Props) {
  const [note, setNote] = useState('');
  const label = lifecycleMeta(targetStatus).label;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div
      className={`fixed inset-0 ${Z.modal} flex items-center justify-center bg-black/40 p-4`}
      onClick={onCancel}
      role="presentation"
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl dark:bg-gray-800"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Schedule before requirements met`}
      >
        <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          {unmet.length} requirement{unmet.length === 1 ? '' : 's'} not yet met
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          This study asks these milestones be cleared before moving a lead to “{label}”:
        </p>

        <ul className="mt-3 space-y-1.5 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-900/40 dark:bg-amber-900/10">
          {unmet.map((u) => (
            <li key={u.key} className="flex items-center gap-2 text-sm text-amber-800 dark:text-amber-300">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden="true" />
              {u.label}
            </li>
          ))}
        </ul>

        <label className="mt-4 block text-xs font-medium text-gray-600 dark:text-gray-300">
          Reason for scheduling anyway <span className="font-normal text-gray-400">(optional)</span>
        </label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value.slice(0, 1000))}
          rows={3}
          autoFocus
          placeholder="e.g. Investigator gave verbal OK; records arriving before the visit."
          className="mt-1 w-full resize-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
        />

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(note.trim())}
            className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1 dark:focus:ring-offset-gray-800"
          >
            Schedule anyway
          </button>
        </div>
      </div>
    </div>
  );
}
